import React, {FC, useEffect, useState} from "react";
import {Icon, Input, InputGroup, InputLeftElement, InputRightElement} from "@chakra-ui/react";
import {Product} from "../../shared/entities/Product/Product";
import {FiChevronDown, FiChevronUp, FiMinus, FiPlus} from "react-icons/fi";
import {hasPermission} from "../../features/Permission/AllowedAccess";
import {productEditPermission} from "../../views/permissions";

type QuantityInputProps = {
    item: Product;
    isViewWithPath?: boolean;
    reset?: boolean;
    onQuantityChange: (updatedItem: Product) => void;
    onQuantityRemove?: (updatedItem: Product) => void;
    width?: string | number;
    height?: string | number;
    isMobile?: boolean;
    error?: boolean;
};
const QuantityInput: FC<QuantityInputProps> = ({
                                                   item,
                                                   isViewWithPath = false,
                                                   reset = false,
                                                   onQuantityChange,
                                                   onQuantityRemove,
                                                   width = 97,
                                                   height = "28px",
                                                   isMobile = false,
                                                   error = false
                                               }) => {
    const [quantity, setQuantity] = useState<number>(item.quantity ? item.quantity : 0);
    if (item.quantity === undefined) {
        item.quantity = 0;
    }

    useEffect(() => {
        if (!reset) {
            setQuantity(item.quantity);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.quantity]);

    useEffect(() => {
        if (reset) {
            setQuantity(0);
        }
    }, [reset]);


    const handleQuantityIncrease = (item: Product, increase: boolean) => {
        let startValue = item.startValue;
        if (hasPermission(productEditPermission)) {
            startValue = item.startAdminValue && item.startAdminValue > 0 ? item.startAdminValue : item.startValue;
        }
        if (increase) {
            setQuantity(prevQuantity => (reset ? 0 : prevQuantity) + (quantity === 0 ? startValue : item.quantityStep));
        } else {
            if (quantity > startValue) {
                setQuantity(prevQuantity => (reset ? 0 : prevQuantity) - item.quantityStep);
            } else {
                if (onQuantityRemove) {
                    onQuantityRemove({...item});
                } else {
                    setQuantity(0);
                }
            }
        }
    };

    const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuantity = parseInt(event.target.value, 10);
        if (!isNaN(newQuantity)) {
            setQuantity(newQuantity);
        }
    };

    useEffect(() => {
        const updatedItem = {...item};
        updatedItem.quantity = quantity;
        onQuantityChange(updatedItem);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quantity]);

    return (
        <InputGroup width={width} zIndex={0}>
            {isMobile &&
                <InputLeftElement height={"100%"}>
                    <FiMinus onClick={() => handleQuantityIncrease(item, false)} size={24}/>
                </InputLeftElement>
            }
            <Input type="number"
                   borderRadius="5px"
                   height={height}
                   fontSize="14px"
                   fontWeight={"sm"}
                   paddingRight={"1em"}
                   paddingLeft={"0.5em"}
                   borderColor={error ? "red.500" : "gray.300"}
                   border={error ? "1px" : "2px"}
                   bgColor={hasPermission(productEditPermission) && item.startAdminValue && item.startAdminValue > 0 && (quantity < item.startValue) ? "red.200" : "undefined"}
                   textAlign={isMobile ? "center" : "left"}
                   value={reset ? 0 : quantity}
                   min={0} onChange={handleQuantityChange}
                   isDisabled={isViewWithPath}
                   readOnly
            />
            {isMobile &&
                <InputRightElement height={"100%"}>
                    <FiPlus onClick={() => handleQuantityIncrease(item, true)} size={24}/>
                </InputRightElement>
            }
            {!isMobile &&
                <InputRightElement mr={-1} height={height} display="flex" flexDirection="column" alignItems="center"
                                   justifyContent="center">
                    <Icon
                        boxSize="14px"
                        as={FiChevronUp}
                        cursor="pointer"
                        onClick={() => handleQuantityIncrease(item, true)}
                    />
                    <Icon
                        boxSize="14px"
                        as={FiChevronDown}
                        cursor="pointer"
                        onClick={() => handleQuantityIncrease(item, false)}
                    />
                </InputRightElement>
            }
        </InputGroup>
    );
}

export default QuantityInput;